import React, {useMemo} from 'react';
import {v4 as uuid} from 'uuid';
import {Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import {LegacyButtonProps} from '@lightricks/react-design-system/dist/components/button/ButtonProps';
import {AssetValidationError} from '@contentlab/api/project/types';
import {
  flowNameForFeature,
  interactionLocationForFeature,
} from '@contentlab/components/feature-editor/utils';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import {
  AssetErrors,
  contentLabActions,
  PreviewAsset,
} from '@contentlab/stores/contentLabStore';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {AssetProps} from '@contentlab/types/projectProps';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets';
import AssetDisplay from './AssetDisplay';
import Guide from './Guide';
import UploadContentAssetError from './UploadContentAssetError';

type UploadContentProps = {
  testID?: string;
  feature: FeatureName;
  title: string;
  guideText: string;
  uploadInteractionId: string;
  readyAssets: Asset[];
  assetErrors: AssetErrors[];
  onLearnMoreClick?: () => void;
};

const UploadContentContainer = styled(Stack)(() => ({
  width: '100%',
  height: '100%',
  minHeight: 0,
  justifyContent: 'flex-start',
  gap: '24px',
  boxSizing: 'border-box',
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  borderRadius: '32px',
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
}));

const GuideContainer = styled(Stack)(() => ({
  paddingInline: '24px',
}));

const FilesScrollableContainer = styled(Stack)(() => ({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  boxSizing: 'border-box',
  padding: '0 24px 24px 24px',
  gap: '24px',
  paddingBottom: '24px',
  ...scrollbarMixins.overrideScrollbarHidden(),
}));

const FilesContainer = styled(Stack)(() => ({
  'width': '100%',
  'height': '100%',
  'boxSizing': 'border-box',
  'gap': '16px',
  '> div:last-child': {
    paddingBottom: '24px',
  },
}));

function UploadContent({
  testID = 'upload-content',
  feature,
  title,
  guideText,
  uploadInteractionId,
  readyAssets,
  assetErrors,
  onLearnMoreClick,
}: UploadContentProps) {
  const {uiInteraction} = useUiInteractionEvent();
  const interactionLocation = interactionLocationForFeature(feature);
  const flowName = flowNameForFeature(feature);
  const {viewPresented} = useViewPresentedEvent();
  const {uploadedVideos: localUploadedVideos, uploadingFiles} =
    useFeatureAssets(feature);

  const {invalidAssetIds, validUploadedVideos} = useMergedAssetErrors({
    serverAssetErrors: assetErrors,
    clientAssetErrors: [],
    uploadedVideos: localUploadedVideos,
  });

  const getFilesMessage = () => {
    const readyCount =
      readyAssets.filter((readyAsset) => !invalidAssetIds.has(readyAsset.id))
        .length || 0;

    const uploadingVideosCount = Math.max(
      validUploadedVideos.length - readyCount,
      0
    );

    const errorVideosCount = localUploadedVideos.filter((uploadedVideo) =>
      invalidAssetIds.has(uploadedVideo.id)
    ).length;

    const readyMessage = translate('ready_assets', {
      count: readyCount,
    });

    const uploadingMessage = translate('uploading_assets', {
      count: uploadingVideosCount,
    });

    const errorMessage = translate('error_assets', {
      count: errorVideosCount,
    });

    return `${[
      readyCount ? readyMessage : null,
      uploadingVideosCount ? uploadingMessage : null,
      errorVideosCount ? errorMessage : null,
    ]
      .filter(Boolean)
      .join(', ')}.`;
  };

  const setPreviewAsset = (asset: AssetProps) => {
    const previewAssets = localUploadedVideos.map((uploadedVideo) => {
      const uploadingFile = uploadingFiles[uploadedVideo.fileName as string];
      return {
        assetId: uploadedVideo.id,
        fileName: uploadedVideo.fileName,
        file: uploadingFile?.file,
      } as PreviewAsset;
    });
    const interactionId = uuid();
    const presentationId = uuid();
    uiInteraction({
      item_name: 'thumbnail_click',
      interaction_location: interactionLocation,
      flow_name: flowName,
      asset_id: asset.id,
      interaction_id: interactionId,
    });
    viewPresented({
      interaction_id: interactionId,
      presentation_id: presentationId,
      view_type: 'popup',
      view_name: CONTENT_LAB_VIEW_NAMES.PREVIEW_ASSET,
      view_location: interactionLocation,
      flow_name: flowName,
      source: 'item_pressed',
    });
    AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.CONTENT_LAB.ASSETS_PREVIEW_MODAL
    ).screen_presentation_id = presentationId;
    contentLabActions.setActivePreviewAssetId(asset.id);
    contentLabActions.setPreviewAssets(previewAssets);
  };

  const uploadMoreButtonStyles: {
    mode: LegacyButtonProps['mode'];
    color: string;
  } = useMemo(() => {
    const defaultStyles = {
      mode: 'tinted' as LegacyButtonProps['mode'],
      color: designSystemToken('semantic.fg.neutral'),
    };

    if (
      (readyAssets.length || assetErrors.length) &&
      readyAssets.every((assetError) => invalidAssetIds.has(assetError.id))
    ) {
      // invert colors if all assets are invalid
      return {
        mode: 'filled' as LegacyButtonProps['mode'],
        color: designSystemToken('semantic.fg.neutral-secondary'),
      };
    }

    return defaultStyles;
  }, [assetErrors, readyAssets, invalidAssetIds]);

  return (
    <UploadContentContainer direction="column" data-testid={testID}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        padding="24px 24px 0 24px"
      >
        <Stack
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          gap="10px"
        >
          <Headline size="md" color={designSystemToken('semantic.fg.primary')}>
            {title}
          </Headline>
          <Stack direction="row" alignItems="center" gap="4px">
            <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
              {getFilesMessage()}
            </Body>
            <ClearAllButton feature={feature} />
          </Stack>
        </Stack>
        <Button
          appearance="neutral"
          mode={uploadMoreButtonStyles.mode}
          size="small"
          startIcon={
            <Icon
              name="Actions-Upload"
              size="medium"
              appearance="primary"
              color={uploadMoreButtonStyles.color}
            />
          }
          onClick={() => {
            uiInteraction({
              item_name: 'upload_more',
              interaction_location: interactionLocation,
              flow_name: flowName,
              interaction_id: uploadInteractionId,
            });
          }}
        >
          <Label size="md" color={uploadMoreButtonStyles.color}>
            {translate('Upload more')}
          </Label>
        </Button>
      </Stack>
      <GuideContainer>
        <Guide
          guideDescription={guideText}
          onLearnMoreClick={onLearnMoreClick}
          sx={{alignSelf: 'flex-start', textAlign: 'start'}}
        />
      </GuideContainer>
      <FilesScrollableContainer direction="column">
        <UploadContentAssetError assetErrors={assetErrors} />
        <FilesContainer>
          {localUploadedVideos.map((video) => {
            const readyAsset = readyAssets?.find((a) => a.id === video.id);
            const assetErrorCodes =
              assetErrors?.find(
                (ae: AssetValidationError) => ae.assetId === video.id
              )?.errorCodes || [];
            const removeVideo = () => {
              uiInteraction({
                item_name: 'remove_asset',
                interaction_location: interactionLocation,
                flow_name: flowName,
                asset_id: video.id,
              });
              contentLabActions.removeUploadedVideo(video.id);
              contentLabActions.removeUploadingFile(video.fileName as string);
              contentLabActions.removeAssetErrors(video.id);
            };

            const uploadingFile = uploadingFiles[video.fileName as string];

            return (
              <AssetDisplay
                key={video.id}
                file={uploadingFile}
                readyAsset={readyAsset}
                onClose={removeVideo}
                feature={feature}
                setPreviewAsset={setPreviewAsset}
                errorCodes={assetErrorCodes}
              />
            );
          })}
        </FilesContainer>
      </FilesScrollableContainer>
    </UploadContentContainer>
  );
}

function ClearAllButton({feature}: {feature: FeatureName}) {
  const {uiInteraction} = useUiInteractionEvent();
  const interactionLocation = interactionLocationForFeature(feature);
  const flowName = flowNameForFeature(feature);
  return (
    <Body
      size="lg"
      color={designSystemToken('semantic.fg.primary')}
      sx={{cursor: 'pointer', textDecoration: 'underline'}}
      onClick={(e) => {
        e.stopPropagation();
        contentLabActions.removeFeatureUploadingFiles(feature);
        contentLabActions.removeFeatureUploadedVideos(feature);
        contentLabActions.removeFeatureAssetErrors(feature);
        uiInteraction({
          item_name: 'clear_all',
          interaction_location: interactionLocation,
          flow_name: flowName,
        });
      }}
    >
      {translate('Clear All')}
    </Body>
  );
}

export default UploadContent;
